import React from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import Button, { ButtonPurpose } from "shared/Button";
import MoneyInput from "shared/MoneyInput";
import * as productsApi from "shared/api/products";
import * as mediaApi from "shared/api/media";
import { errorMessage } from "shared/api/client";
import ProductImageCard from "../ProductImageCard";
import editorStyles from "../../ProductEditor.module.css";
import { sizeSortKey } from "../../sizeOrder";
import { resolveColour, nameForHex } from "./colours";
import SwatchInput from "./SwatchInput";
import styles from "./index.module.css";

const STANDARD_SIZES = ["S", "M", "L", "XL", "2X", "3X", "Universal"];

// "X-Large" -> "XL", "One Size Fits All" -> "Universal". Anything else is
// shown as its own chip.
function sizeChip(name) {
  const k = String(name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
  const map = {
    small: "S",
    s: "S",
    sm: "S",
    medium: "M",
    m: "M",
    md: "M",
    large: "L",
    l: "L",
    lg: "L",
    xlarge: "XL",
    xl: "XL",
    xxlarge: "2X",
    "2xlarge": "2X",
    "2x": "2X",
    xxl: "2X",
    "2xl": "2X",
    xxxlarge: "3X",
    "3xlarge": "3X",
    "3x": "3X",
    xxxl: "3X",
    "3xl": "3X",
    onesizefitsall: "Universal",
    onesize: "Universal",
    universal: "Universal",
  };
  return map[k] || name;
}

const toCents = (dollars) =>
  dollars == null || !Number.isFinite(Number(dollars))
    ? null
    : Math.round(Number(dollars) * 100);

/**
 * One tab per colour. A colour is either a whole variation (Long Sleeve: one
 * variation per colour, with its own images) or part of one (Polo: one
 * variation, colour in the size name "Black / Large").
 */
export function colourGroups(variations) {
  const groups = [];
  (variations || []).forEach((v) => {
    const colours =
      v.colours && v.colours.length
        ? v.colours
        : [{ key: v.name, label: v.name, swatchHex: null }];
    const multi = colours.length > 1;
    colours.forEach((c) => {
      const sizes = (v.variants || [])
        .filter((s) => {
          if (!multi) return true;
          const name = String(s.name || "");
          return (
            name.includes(" / ") && name.split(" / ")[0].trim() === c.label
          );
        })
        .map((s) => ({
          ...s,
          sizeName: String(s.name || "").includes(" / ")
            ? String(s.name).split(" / ").slice(1).join(" / ").trim()
            : s.name,
        }))
        .sort((a, b) => {
          // "X-Large" -> "XLarge", which the size table knows.
          const ka = sizeSortKey(String(a.sizeName).replace(/-/g, ""));
          const kb = sizeSortKey(String(b.sizeName).replace(/-/g, ""));
          return ka < kb ? -1 : ka > kb ? 1 : 0;
        });
      groups.push({
        id: `${v.id}:${c.key}`,
        variationId: v.id,
        colourKey: c.key,
        label: c.label,
        swatchHex: c.swatchHex || null,
        sizes,
        ownsImages: !multi && !v.sharesProductImages,
      });
    });
  });
  return groups;
}

// Editable working copy, per SQL variation (the shape the API takes).
const toDraft = (variations) =>
  (variations || []).map((v) => ({
    id: v.id,
    swatches: Object.fromEntries(
      (v.colours || []).map((c) => [c.key, c.swatchHex || null])
    ),
    images: [...(v.images || [])],
    variants: (v.variants || []).map((s) => ({
      id: s.id,
      memberPriceCents: toCents(s.memberPrice),
      availableOnline: s.availableOnline,
    })),
  }));

/**
 * Only what changed, in the shape PATCH /v1/products/:id `variationEdits`
 * takes. A cleared price is left alone (never sent as 0).
 */
export function diffVariations(original, draft) {
  const edits = [];
  (draft || []).forEach((d) => {
    const o = (original || []).find((v) => v.id === d.id);
    if (!o) return;
    const edit = { id: d.id };
    const swatches = {};
    (o.colours || []).forEach((c) => {
      const next = (d.swatches || {})[c.key] || null;
      if (next !== (c.swatchHex || null)) swatches[c.key] = next;
    });
    if (Object.keys(swatches).length) edit.swatches = swatches;
    if (
      !o.sharesProductImages &&
      JSON.stringify(d.images) !== JSON.stringify(o.images || [])
    ) {
      edit.images = d.images;
    }
    const variants = [];
    (d.variants || []).forEach((ds) => {
      const os = (o.variants || []).find((s) => s.id === ds.id);
      if (!os) return;
      const ve = { id: ds.id };
      if (
        typeof ds.memberPriceCents === "number" &&
        ds.memberPriceCents !== toCents(os.memberPrice)
      ) {
        ve.memberPrice = ds.memberPriceCents / 100;
      }
      if (
        typeof ds.availableOnline === "boolean" &&
        ds.availableOnline !== os.availableOnline
      ) {
        ve.availableOnline = ds.availableOnline;
      }
      if (Object.keys(ve).length > 1) variants.push(ve);
    });
    if (variants.length) edit.variants = variants;
    if (Object.keys(edit).length > 1) edits.push(edit);
  });
  return edits;
}

/**
 * "Edit Variation" (Figma: WWG Long Sleeve / Mesh Hat - Edit; BugHerd #67).
 *
 * The product's colours and sizes as the storefront sells them. Per colour: a
 * swatch (typed as a name or an HTML code) and, when the colour has its own
 * item, images. Per size: PM price and Available / Out of Stock, with
 * NetSuite's stock read-only. Saved on its own Save, straight to the store:
 * these are live store settings like stock, not part of Push Revisions.
 */
export default function StoreVariationsCard({ productId, variations, onSaved }) {
  const groups = React.useMemo(() => colourGroups(variations), [variations]);
  const [draft, setDraft] = React.useState(() => toDraft(variations));
  const [swatchText, setSwatchText] = React.useState({});
  const [selectedId, setSelectedId] = React.useState(null);
  const [expanded, setExpanded] = React.useState({});
  const [busy, setBusy] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);

  React.useEffect(() => {
    setDraft(toDraft(variations));
    setSwatchText({});
  }, [variations]);

  const selected =
    groups.find((g) => g.id === selectedId) || groups[0] || null;

  // Figma shows the first size opened, so price / availability / stock are
  // visible without hunting for them. Others open on Expand.
  const isOpen = (group, sizeId) =>
    expanded[sizeId] !== undefined
      ? expanded[sizeId]
      : group.sizes[0] && group.sizes[0].id === sizeId;

  const edits = React.useMemo(
    () => diffVariations(variations, draft),
    [variations, draft]
  );
  const dirty = edits.length > 0;

  const patchVariation = (id, fn) =>
    setDraft((prev) => prev.map((v) => (v.id === id ? fn(v) : v)));
  const patchSize = (variationId, sizeId, patch) =>
    patchVariation(variationId, (v) => ({
      ...v,
      variants: v.variants.map((s) =>
        s.id === sizeId ? { ...s, ...patch } : s
      ),
    }));

  const draftFor = (g) => draft.find((v) => v.id === g.variationId);
  const swatchOf = (g) => (draftFor(g)?.swatches || {})[g.colourKey] || null;
  const setSwatch = (g, hex) =>
    patchVariation(g.variationId, (v) => ({
      ...v,
      swatches: { ...v.swatches, [g.colourKey]: hex },
    }));

  const onSwatchText = (g, text) => {
    setSwatchText((prev) => ({ ...prev, [g.id]: text }));
    if (!text.trim()) {
      setSwatch(g, null);
      return;
    }
    const hex = resolveColour(text);
    if (hex) setSwatch(g, hex);
  };

  const addImages = async (g, files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setUploading(true);
    try {
      const uploaded = await Promise.all(
        list.map((f) => mediaApi.upload(f).catch(() => null))
      );
      const urls = uploaded.map((m) => m && m.url).filter(Boolean);
      if (urls.length < list.length) {
        toast.error(`${list.length - urls.length} file(s) failed to upload.`);
      }
      patchVariation(g.variationId, (v) => ({
        ...v,
        images: [...v.images, ...urls],
      }));
    } finally {
      setUploading(false);
    }
  };

  const discard = () => {
    setDraft(toDraft(variations));
    setSwatchText({});
  };

  const save = async () => {
    if (!dirty || busy) return;
    setBusy(true);
    try {
      const updated = await productsApi.update(productId, {
        variationEdits: edits,
      });
      const untracked = updated?.variationSync?.untracked || [];
      if (untracked.length) {
        toast(
          `Saved. ${untracked.length} size${
            untracked.length === 1 ? " has" : "s have"
          } no NetSuite stock record, so availability wasn't changed.`
        );
      } else {
        toast.success("Variations saved to the store.");
      }
      if (onSaved) onSaved(updated);
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't save the variations."));
    } finally {
      setBusy(false);
    }
  };

  if (!groups.length) {
    return (
      <p className={styles.empty}>
        No Product Variations Available (Add Variations to the Product in
        Netsuite)
      </p>
    );
  }

  const g = selected;
  const d = draftFor(g) || { variants: [], images: [] };
  const hex = swatchOf(g);
  const typed = swatchText[g.id];
  const shownText = typed !== undefined ? typed : nameForHex(hex) || hex || "";
  const present = new Set(g.sizes.map((s) => sizeChip(s.sizeName)));
  const extraChips = [...present].filter((c) => !STANDARD_SIZES.includes(c));

  return (
    <div className={styles.wrap}>
      {groups.length > 1 && (
        <div className={styles.tabs} role="tablist" aria-label="Colours">
          {groups.map((x) => {
            const xHex = swatchOf(x);
            return (
              <button
                key={x.id}
                type="button"
                role="tab"
                aria-selected={x.id === g.id}
                className={`${styles.tab} ${
                  x.id === g.id ? styles.tabActive : ""
                }`}
                onClick={() => setSelectedId(x.id)}
                title={`Edit ${x.label}`}
              >
                <span
                  className={styles.dot}
                  style={{ background: xHex || "transparent" }}
                />
                {x.label}
              </button>
            );
          })}
        </div>
      )}

      <div className={styles.card}>
        <div className={styles.header}>
          <span className={styles.heading}>Edit Variation - {g.label}</span>
          <div className={styles.headerActions}>
            <Button
              type="button"
              purpose={ButtonPurpose.save}
              title="Save swatches, images, prices and availability to the store now. (Separate from Push Revisions.)"
              disabled={!dirty || busy || uploading}
              onClick={save}
            >
              {busy ? "Saving…" : "Save"}
            </Button>
            <Button
              type="button"
              title="Undo the variation changes you haven't saved."
              disabled={!dirty || busy}
              onClick={discard}
            >
              Cancel
            </Button>
          </div>
        </div>

        <h4 className={styles.sectionTitle}>Color Swatch</h4>
        <div className={styles.swatchRow}>
          {/* The swatch is also the hue picker (BugHerd #90): click it to pick
              any colour; the field beside it takes a name or HTML code. */}
          <label
            className={`${styles.swatchBox} ${hex ? styles.swatchBoxSet : ""}`}
            style={{ background: hex || "transparent" }}
            title="Pick a colour"
          >
            {hex && <span className={styles.check}>✓</span>}
            <input
              type="color"
              className={styles.hiddenPicker}
              aria-label={`Pick a colour for ${g.label}`}
              value={hex || "#ffffff"}
              onChange={(e) =>
                onSwatchText(g, nameForHex(e.target.value) || e.target.value)
              }
            />
          </label>
          <SwatchInput
            id={`swatch-${g.id}`}
            label={`Swatch for ${g.label}`}
            value={shownText}
            onChange={(text) => onSwatchText(g, text)}
          />
        </div>

        <h4 className={styles.sectionTitle}>Sizing</h4>
        <div className={styles.chips}>
          {[...STANDARD_SIZES, ...extraChips].map((chip) => (
            <span
              key={chip}
              className={`${styles.chip} ${
                present.has(chip) ? styles.chipOn : ""
              }`}
              title={
                present.has(chip)
                  ? `${chip} is offered in ${g.label}`
                  : `${chip} isn't offered in ${g.label} (sizes come from NetSuite)`
              }
            >
              {chip}
            </span>
          ))}
        </div>

        <h4 className={styles.sectionTitle}>SKUs</h4>
        <ul className={styles.skuList}>
          {g.sizes.map((s) => {
            const ds = d.variants.find((x) => x.id === s.id) || {};
            const open = Boolean(isOpen(g, s.id));
            const tracked = s.stock != null;
            return (
              <li key={s.id} className={styles.skuItem}>
                <div className={styles.skuRow}>
                  <span className={styles.skuSize}>{s.sizeName}</span>
                  <span className={styles.skuBox}>
                    <span className={styles.skuTag}>SKU</span>
                    <span className={styles.skuValue}>{s.sku}</span>
                  </span>
                  <button
                    type="button"
                    className={`${styles.expandBtn} ${
                      open ? styles.expandBtnOpen : ""
                    }`}
                    aria-expanded={open}
                    aria-label={`${open ? "Collapse" : "Expand"} ${s.name}`}
                    onClick={() =>
                      setExpanded((prev) => ({ ...prev, [s.id]: !open }))
                    }
                    title={
                      open
                        ? "Hide this size's settings."
                        : "Show price, availability and stock for this size."
                    }
                  >
                    {open ? "Collapse" : "Expand"}
                  </button>
                </div>

                {open && (
                  <div className={styles.skuDetail}>
                    <h5 className={styles.subTitle}>Pricing</h5>
                    <div className={styles.field}>
                      <label
                        className={styles.fieldLabel}
                        htmlFor={`pm-${s.id}`}
                      >
                        Premier Member Price
                      </label>
                      <span className={styles.money}>
                        <span className={styles.dollar}>$</span>
                        <MoneyInput
                          id={`pm-${s.id}`}
                          value={ds.memberPriceCents}
                          onChange={(cents) =>
                            patchSize(g.variationId, s.id, {
                              memberPriceCents: cents,
                            })
                          }
                          placeholder="00.00"
                        />
                      </span>
                    </div>

                    <h5 className={styles.subTitle}>Availability</h5>
                    <div className={styles.field}>
                      <div className={editorStyles.statusButtons}>
                        {[
                          { on: true, label: "Available" },
                          { on: false, label: "Out of Stock" },
                        ].map((opt) => {
                          const active = ds.availableOnline === opt.on;
                          const cls = opt.on
                            ? active
                              ? editorStyles.availabilityAvailableActive
                              : editorStyles.availabilityAvailable
                            : active
                            ? editorStyles.availabilityOutOfStockActive
                            : editorStyles.availabilityOutOfStock;
                          return (
                            <button
                              key={opt.label}
                              type="button"
                              disabled={!tracked}
                              aria-pressed={active}
                              aria-label={`${s.name} ${opt.label}`}
                              className={`${editorStyles.statusButton} ${cls}${
                                tracked
                                  ? ""
                                  : ` ${editorStyles.statusButtonDisabled}`
                              }`}
                              title={
                                tracked
                                  ? `Set ${s.name} to ${opt.label}.`
                                  : "This size has no NetSuite stock record, so its availability can't be set here."
                              }
                              onClick={() =>
                                patchSize(g.variationId, s.id, {
                                  availableOnline: opt.on,
                                })
                              }
                            >
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <h5 className={styles.subTitle}>Stock</h5>
                    <div className={styles.field}>
                      <span
                        className={styles.stockBox}
                        title={
                          tracked
                            ? `${s.stock.onHand} on hand, ${s.stock.reserved} reserved (from NetSuite)`
                            : "Not tracked in NetSuite"
                        }
                      >
                        <span className={styles.skuTag}>Current Stock</span>
                        <span className={styles.skuValue}>
                          {tracked ? s.stock.available : "N/A"}
                        </span>
                      </span>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>

        {g.ownsImages ? (
          <div className={styles.images}>
            <ProductImageCard
              images={d.images}
              onSelectFile={(files) => addImages(g, files)}
              onChange={(next) =>
                patchVariation(g.variationId, (v) => ({
                  ...v,
                  images: next.map((img) =>
                    typeof img === "string" ? img : img.url
                  ),
                }))
              }
            />
          </div>
        ) : (
          <p className={styles.note}>
            {g.label} uses the product&apos;s own images (Product Images card).
          </p>
        )}
      </div>
    </div>
  );
}

const variantShape = PropTypes.shape({
  id: PropTypes.number,
  sku: PropTypes.string,
  name: PropTypes.string,
  memberPrice: PropTypes.number,
  stock: PropTypes.shape({
    onHand: PropTypes.number,
    reserved: PropTypes.number,
    available: PropTypes.number,
  }),
  availableOnline: PropTypes.bool,
});

StoreVariationsCard.propTypes = {
  productId: PropTypes.string.isRequired,
  variations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      sku: PropTypes.string,
      name: PropTypes.string,
      sharesProductImages: PropTypes.bool,
      images: PropTypes.arrayOf(PropTypes.string),
      colours: PropTypes.arrayOf(
        PropTypes.shape({
          key: PropTypes.string,
          label: PropTypes.string,
          swatchHex: PropTypes.string,
        })
      ),
      variants: PropTypes.arrayOf(variantShape),
    })
  ),
  onSaved: PropTypes.func,
};

StoreVariationsCard.defaultProps = {
  variations: [],
  onSaved: null,
};
